/** The subset of a node this needs: where it sits and how big it is. */
export interface ViewportNode {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Viewport {
  pan: { x: number; y: number };
  zoom: number;
}

/** Breathing room between the content and the edge of the board, in px. */
const PADDING = 40;

/**
 * Keep nodes legible when a large map cannot fit. A phone starts at full node
 * size; larger boards can zoom out moderately before framing the first node.
 */
const MIN_ZOOM = 0.65;
const MAX_ZOOM = 1;

/** Where a map with nothing in it starts, and the historical default. */
export const DEFAULT_VIEWPORT: Viewport = { pan: { x: PADDING, y: PADDING }, zoom: 1 };

/**
 * A view that frames the whole map when it can remain readable, otherwise its
 * first node.
 *
 * The board used to open at a fixed pan of (40, 40) at zoom 1, which assumed
 * the content began near the origin. A map's first node is created at (360,
 * 220), so on a phone -- roughly 320px of visible board -- it sat off the right
 * edge and the map looked empty. The "centre" control reset to that same fixed
 * origin, so it could not recover the view either.
 *
 * Never zooms IN past 1: a single small node blown up to fill the board looks
 * broken, and the point is to show what is there, not to magnify it.
 */
export function fitToContent(
  nodes: readonly ViewportNode[],
  boardWidth: number,
  boardHeight: number
): Viewport {
  if (nodes.length === 0 || boardWidth <= 0 || boardHeight <= 0) return DEFAULT_VIEWPORT;

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const node of nodes) {
    minX = Math.min(minX, node.x);
    minY = Math.min(minY, node.y);
    maxX = Math.max(maxX, node.x + node.width);
    maxY = Math.max(maxY, node.y + node.height);
  }

  const contentWidth = Math.max(1, maxX - minX);
  const contentHeight = Math.max(1, maxY - minY);
  const availableWidth = Math.max(1, boardWidth - PADDING * 2);
  const availableHeight = Math.max(1, boardHeight - PADDING * 2);
  const narrowBoard = boardWidth < 480;
  const minimumZoom = narrowBoard ? 1 : MIN_ZOOM;

  const zoom = Math.min(
    MAX_ZOOM,
    Math.max(minimumZoom, Math.min(availableWidth / contentWidth, availableHeight / contentHeight))
  );

  const allNodesFit = contentWidth * zoom <= availableWidth && contentHeight * zoom <= availableHeight;
  if (!allNodesFit) {
    const first = nodes[0];
    return {
      zoom,
      pan: {
        x: boardWidth / 2 - (first.x + first.width / 2) * zoom,
        y: narrowBoard ? PADDING - first.y * zoom : boardHeight / 2 - (first.y + first.height / 2) * zoom,
      },
    };
  }

  // Centre the content: the leftover space is split either side, then the
  // content's own origin is cancelled out.
  return {
    zoom,
    pan: {
      x: (boardWidth - contentWidth * zoom) / 2 - minX * zoom,
      y: (narrowBoard ? PADDING : (boardHeight - contentHeight * zoom) / 2) - minY * zoom,
    },
  };
}
